import React, { useState, useEffect } from 'react'
import { api } from '@/lib/api'
import { useToast } from '@/context/ToastContext'
import { Modal } from '@/shared/components/Modal'
import type { ModalName } from '@/lib/modalTelemetry'
import s from '@/shared/components/ModuleModals.module.css'

// ── Shared modal shell ────────────────────────────────────────────────────────
// Thin wrapper over the shared <Modal> that renders the `// namespace/name`
// subtitle convention these module-management dialogs share.

function ModalShell({
  track,
  namespace,
  name,
  title,
  onClose,
  children,
}: {
  /** Which of these dialogs this is, for telemetry - the shared shell cannot name itself. */
  track: ModalName
  namespace: string
  name: string
  title: string
  onClose: () => void
  children: React.ReactNode
}) {
  return (
    <Modal track={track} title={title} subtitle={`// ${namespace}/${name}`} maxWidth={440} onClose={onClose}>
      {children}
    </Modal>
  )
}

// ── DeprecateModuleModal ──────────────────────────────────────────────────────

export function DeprecateModuleModal({ namespace, name, provider, onClose, onDeprecated }: {
  namespace: string; name: string; provider: string
  onClose: () => void; onDeprecated: () => void
}) {
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.put(`/v1/modules/${namespace}/${name}/${provider}/deprecate`, {
        message: message.trim() || null,
      })
      onDeprecated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Deprecation failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell track="module_deprecate" namespace={namespace} name={name} title="Deprecate module" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className={s.noticeAmber}>
          <div className={`${s.noticeHeadline}`} style={{ color: 'var(--amber)' }}>
            ⚠ Deprecating marks this entire module as superseded.
          </div>
          <div className={s.noticeBody}>
            <strong>Every version</strong> is marked deprecated (now and any published
            later). All versions remain downloadable. Org members receive a deprecation
            warning by email.
          </div>
        </div>

        <div className={s.fieldRow}>
          <label className="field-label">
            Message <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional)</span>
          </label>
          <textarea
            className="input"
            rows={3}
            placeholder="e.g. Use vpc-v2 instead - this module is no longer maintained"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            autoFocus
            style={{ resize: 'vertical' }}
          />
        </div>

        {error && <div className="feedback-error" style={{ marginBottom: 14 }}>{error}</div>}

        <div className={s.footerRow}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button type="submit" className={`btn btn-primary btn-sm ${s.btnAmber}`} disabled={busy}>
            {busy ? <span className="spinner" /> : 'Confirm deprecate'}
          </button>
        </div>
      </form>
    </ModalShell>
  )
}

// ── UndeprecateModuleModal ────────────────────────────────────────────────────

export function UndeprecateModuleModal({ namespace, name, provider, onClose, onUndeprecated }: {
  namespace: string; name: string; provider: string
  onClose: () => void; onUndeprecated: () => void
}) {
  const [busy, setBusy] = useState(false)
  const { showToast } = useToast()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      await api.delete(`/v1/modules/${namespace}/${name}/${provider}/deprecate`)
      onUndeprecated()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Undeprecate failed', 'error')
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell track="module_undeprecate" namespace={namespace} name={name} title="Undeprecate module" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className={s.noticeGreen}>
          <div className={s.noticeHeadline} style={{ color: 'var(--green)' }}>
            ✓ Restoring {namespace}/{name} to active status.
          </div>
          <div className={s.noticeBody}>
            The deprecation notice is removed from <strong>every version</strong>. This also
            reactivates any version that was deprecated individually (e.g. for a security
            issue) and clears its message - re-deprecate those versions afterwards if needed.
          </div>
        </div>

        <div className={s.footerRow}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
            {busy ? <span className="spinner" /> : 'Confirm undeprecate'}
          </button>
        </div>
      </form>
    </ModalShell>
  )
}

// ── DeprecateModal ────────────────────────────────────────────────────────────

export interface DeprecateModalProps {
  namespace: string
  name: string
  provider: string
  /** If provided, version picker is hidden and this version is used directly */
  fixedVersion?: string
  /** Used as the initial selection when fixedVersion is absent */
  latestVersion: string
  dependentCount?: number
  onClose: () => void
  onDeprecated: () => void
}

export function DeprecateModal({
  namespace,
  name,
  provider,
  fixedVersion,
  latestVersion,
  dependentCount = 0,
  onClose,
  onDeprecated,
}: DeprecateModalProps) {
  const [message, setMessage] = useState('')
  const [selectedVersion, setSelectedVersion] = useState(fixedVersion ?? latestVersion)
  const [versions, setVersions] = useState<string[]>([fixedVersion ?? latestVersion])
  const [versionsLoading, setVersionsLoading] = useState(!fixedVersion)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (fixedVersion) return
    api.get<{ versions: { version: string }[] }>(`/api/namespaces/${namespace}/modules/${name}/versions`)
      .then((res) => {
        const vList = res.versions.map((v) => v.version)
        if (vList.length > 0) { setVersions(vList); setSelectedVersion(vList[0]) }
      })
      .catch(() => {})
      .finally(() => setVersionsLoading(false))
  }, [namespace, name, fixedVersion])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.put(`/v1/modules/${namespace}/${name}/${provider}/${selectedVersion}/deprecate`, {
        message: message.trim() || null,
      })
      onDeprecated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Deprecation failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell track="module_version_deprecate" namespace={namespace} name={name} title="Deprecate version" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className={s.noticeAmber}>
          <div className={s.noticeHeadline} style={{ color: 'var(--amber)', marginBottom: dependentCount > 0 ? 4 : 0 }}>
            ⚠ Deprecating discourages use but does not block downloads.
          </div>
          {dependentCount > 0 && (
            <div className={s.noticeBody}>
              {dependentCount} module{dependentCount !== 1 ? 's depend' : ' depends'} on this
            </div>
          )}
        </div>

        {fixedVersion ? (
          <div className={s.fixedVersion}>
            Version: <span style={{ color: 'var(--amber)' }}>v{fixedVersion}</span>
          </div>
        ) : (
          <div className={s.fieldRowSm}>
            <label className="field-label">Version <span style={{ color: 'var(--red)' }}>*</span></label>
            <select
              className="input"
              value={selectedVersion}
              onChange={(e) => setSelectedVersion(e.target.value)}
              disabled={versionsLoading}
            >
              {versions.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        )}

        <div className={s.fieldRow}>
          <label className="field-label">
            Deprecation message <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional)</span>
          </label>
          <textarea
            className="input"
            rows={3}
            placeholder="e.g. Use vpc-v2 instead - this version has a known routing issue"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            autoFocus
            style={{ resize: 'vertical' }}
          />
        </div>

        {error && <div className="feedback-error" style={{ marginBottom: 14 }}>{error}</div>}

        <div className={s.footerRow}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            className={`btn btn-primary btn-sm ${s.btnAmber}`}
            disabled={busy || !selectedVersion}
          >
            {busy ? <span className="spinner" /> : 'Confirm deprecate'}
          </button>
        </div>
      </form>
    </ModalShell>
  )
}

// ── BlockModal ────────────────────────────────────────────────────────────────

export interface BlockModalProps {
  namespace: string
  name: string
  provider: string
  version: string
  dependentCount?: number
  onClose: () => void
  onBlocked: () => void
}

export function BlockModal({ namespace, name, provider, version, dependentCount = 0, onClose, onBlocked }: BlockModalProps) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!reason.trim()) return
    setBusy(true)
    setError(null)
    try {
      await api.post(`/v1/modules/${namespace}/${name}/${provider}/${version}/block`, {
        reason: reason.trim(),
      })
      onBlocked()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Block failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell track="module_block" namespace={namespace} name={name} title="Block consumption" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className={s.noticeRed}>
          <div className={s.noticeHeadline} style={{ color: 'var(--red)' }}>
            ✗ Blocking <strong>v{version}</strong> of {namespace}/{name}
          </div>
          <div className={s.noticeBody}>
            Terraform will be unable to download this version. Reversible from the module detail page.
          </div>
          {dependentCount > 0 && (
            <div className={s.noticeSub}>
              ⚠ {dependentCount} module{dependentCount !== 1 ? 's depend' : ' depends'} on this
            </div>
          )}
        </div>

        <div className={s.fieldRow}>
          <label className="field-label">Reason <span style={{ color: 'var(--red)' }}>*</span></label>
          <textarea
            className="input"
            rows={3}
            placeholder="e.g. Critical vulnerability CVE-2024-1234 - use v2.3.1 instead"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
            style={{ resize: 'vertical' }}
          />
        </div>

        {error && <div className="feedback-error" style={{ marginBottom: 14 }}>{error}</div>}

        <div className={s.footerRow}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            className="btn btn-danger btn-sm"
            disabled={busy || !reason.trim()}
          >
            {busy ? <span className="spinner" /> : 'Confirm block'}
          </button>
        </div>
      </form>
    </ModalShell>
  )
}

// ── OverrideModal ─────────────────────────────────────────────────────────────

export interface OverrideModalProps {
  namespace: string
  name: string
  provider: string
  version: string
  onClose: () => void
  onOverridden: () => void
}

export function OverrideModal({ namespace, name, provider, version, onClose, onOverridden }: OverrideModalProps) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!reason.trim()) return
    setBusy(true)
    setError(null)
    try {
      await api.post(`/v1/modules/${namespace}/${name}/${provider}/${version}/override-consumable`, {
        consumable: true,
        reason: reason.trim(),
      })
      onOverridden()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Override failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell track="module_policy_override" namespace={namespace} name={name} title="Override policy block" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className={s.noticeAmber}>
          <div className={s.noticeHeadline} style={{ color: 'var(--amber)' }}>
            ⚠ Overriding <strong>v{version}</strong> of {namespace}/{name}
          </div>
          <div className={s.noticeBody}>
            Policy failures will be ignored and Terraform will be able to download this version.
          </div>
        </div>

        <div className={s.fieldRow}>
          <label className="field-label">Reason <span style={{ color: 'var(--red)' }}>*</span></label>
          <input
            className="input"
            placeholder="e.g. Approved by security team - ticket SEC-123"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
          />
        </div>

        {error && <div className="feedback-error" style={{ marginBottom: 14 }}>{error}</div>}

        <div className={s.footerRow}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            className={`btn btn-primary btn-sm ${s.btnAmber}`}
            disabled={busy || !reason.trim()}
          >
            {busy ? <span className="spinner" /> : 'Confirm override'}
          </button>
        </div>
      </form>
    </ModalShell>
  )
}

// ── UnblockModal ──────────────────────────────────────────────────────────────

export interface UnblockModalProps {
  namespace: string
  name: string
  provider: string
  version: string
  onClose: () => void
  onUnblocked: () => void
}

export function UnblockModal({ namespace, name, provider, version, onClose, onUnblocked }: UnblockModalProps) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!reason.trim()) return
    setBusy(true)
    setError(null)
    try {
      await api.post(`/v1/modules/${namespace}/${name}/${provider}/${version}/unblock`, { reason: reason.trim() })
      onUnblocked()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unblock failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell track="module_unblock" namespace={namespace} name={name} title="Unblock consumption" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className={s.noticeGreen}>
          <div className={s.noticeHeadline} style={{ color: 'var(--green)' }}>
            ✓ Unblocking <strong>v{version}</strong> of {namespace}/{name}
          </div>
          <div className={s.noticeBody}>
            Clears the manual block. If this version is also failing an enforcing policy, that
            policy block is overridden so Terraform can download it; otherwise the version
            returns to normal policy evaluation.
          </div>
        </div>

        <div className={s.fieldRow}>
          <label className="field-label">Reason <span style={{ color: 'var(--red)' }}>*</span></label>
          <textarea
            className="input"
            rows={3}
            placeholder="e.g. False positive - approved by security, ticket SEC-456"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
            style={{ resize: 'vertical' }}
          />
        </div>

        {error && <div className="feedback-error" style={{ marginBottom: 14 }}>{error}</div>}

        <div className={s.footerRow}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy || !reason.trim()}>
            {busy ? <span className="spinner" /> : 'Confirm unblock'}
          </button>
        </div>
      </form>
    </ModalShell>
  )
}

// ── UndeprecateModal ──────────────────────────────────────────────────────────

export interface UndeprecateModalProps {
  namespace: string
  name: string
  provider: string
  version: string
  onClose: () => void
  onUndeprecated: () => void
}

export function UndeprecateModal({ namespace, name, provider, version, onClose, onUndeprecated }: UndeprecateModalProps) {
  const [busy, setBusy] = useState(false)
  const { showToast } = useToast()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      await api.delete(`/v1/modules/${namespace}/${name}/${provider}/${version}/deprecate`)
      onUndeprecated()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Undeprecate failed', 'error')
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell track="module_version_undeprecate" namespace={namespace} name={name} title="Undeprecate version" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className={s.noticeGreen}>
          <div className={s.noticeHeadline} style={{ color: 'var(--green)' }}>
            ✓ Restoring <strong>v{version}</strong> of {namespace}/{name}
          </div>
          <div className={s.noticeBody}>
            The deprecation notice will be removed and the version will be marked as current.
          </div>
        </div>

        <div className={s.footerRow}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
            {busy ? <span className="spinner" /> : 'Confirm undeprecate'}
          </button>
        </div>
      </form>
    </ModalShell>
  )
}
